/**
 * Where the engineer is, and what Back means.
 *
 * Five screens, one line of descent: projects → what is being tested → boards →
 * the lineup on one board → one breaker (or that board's own script). Nothing
 * here touches React or the URL, so the rules that actually matter are testable
 * without a browser.
 *
 * Three of those rules are not cosmetic:
 *
 *  - **Changing project resets everything below it.** A board id from MY02 is
 *    meaningless inside MY03, and a stale one that happens to exist there would
 *    put an engineer on the wrong equipment with no sign anything was wrong.
 *  - **Changing what is being tested resets the register too.** An LV board id
 *    means nothing once the target is MV switchgear, and the silo rule in
 *    `equipment-silo.md` is only real if the navigation enforces it.
 *  - **Back never leaves the app.** On an installed PWA there is no browser
 *    chrome to fall back to; at the top of the stack Back does nothing rather
 *    than closing the window mid-capture.
 */

import { type TestTarget, makeTarget } from '../domain/equipment.js';

export type Route =
  | { screen: 'projects' }
  | { screen: 'target' }
  | { screen: 'boards' }
  | { screen: 'lineup'; boardId: number }
  | { screen: 'boardScript'; boardId: number }
  | { screen: 'breaker'; boardId: number; breakerRef: string };

export interface NavState {
  /** Null until a project is chosen; every read below depends on it. */
  projectId: number | null;
  /**
   * What is being tested: project, equipment type and Cx level, as one value.
   * Null until chosen. Nothing below the `target` screen renders without it,
   * because the script, the shot list and the report format all come from it.
   */
  target: TestTarget | null;
  route: Route;
}

export const initialNav = (): NavState => ({ projectId: null, target: null, route: { screen: 'projects' } });

/** The screen one step up. `projects` is the top: Back there does nothing. */
export function parentOf(route: Route): Route {
  switch (route.screen) {
    case 'projects':
      return { screen: 'projects' };
    case 'target':
      return { screen: 'projects' };
    case 'boards':
      return { screen: 'target' };
    case 'lineup':
    case 'boardScript':
      return { screen: 'boards' };
    case 'breaker':
      return { screen: 'lineup', boardId: route.boardId };
  }
}

export function back(state: NavState): NavState {
  if (state.route.screen === 'projects') return state;
  return { ...state, route: parentOf(state.route) };
}

/**
 * Chooses a project and drops everything that belonged to the last one.
 *
 * Re-choosing the same project still resets: it is how an engineer says "take me
 * back to the top", and keeping a deep route would make that button do nothing.
 */
export function chooseProject(_state: NavState, projectId: number): NavState {
  return { projectId, target: null, route: { screen: 'target' } };
}

/**
 * Chooses the equipment type and Cx level, and drops any register position held
 * against the previous target.
 *
 * `makeTarget` refuses a level with no content and an equipment type the
 * platform does not test, so an unbuildable target cannot be navigated into —
 * the screen disables those options, and this refuses them again.
 */
export function chooseTarget(state: NavState, equipmentType: string, level: string): NavState {
  if (state.projectId === null) return state;
  const target = makeTarget(state.projectId, equipmentType, level);
  return { projectId: state.projectId, target, route: { screen: 'boards' } };
}

/** Leaves the project entirely. Used on sign-out and on "choose another project". */
export function leaveProject(): NavState {
  return initialNav();
}

export function openBoard(state: NavState, boardId: number): NavState {
  return requireProject(state, { screen: 'lineup', boardId });
}

export function openBoardScript(state: NavState, boardId: number): NavState {
  return requireProject(state, { screen: 'boardScript', boardId });
}

export function openBreaker(state: NavState, boardId: number, breakerRef: string): NavState {
  return requireProject(state, { screen: 'breaker', boardId, breakerRef });
}

/**
 * Refuses to navigate below the project picker with no project chosen.
 *
 * Belt and braces: the screens make it unreachable, but a deep route with a null
 * project would read a list with no scope, and the scoped client is the only
 * thing standing between one project's register and another's.
 */
function requireProject(state: NavState, route: Route): NavState {
  if (state.projectId === null || state.target === null) return state;
  return { ...state, route };
}

/** The label the Back control should carry, so it names a place rather than a direction. */
export function backLabel(route: Route, names: { board?: string | undefined } = {}): string | null {
  switch (route.screen) {
    case 'projects':
      return null;
    case 'target':
      return 'Projects';
    case 'boards':
      return 'What to test';
    case 'lineup':
    case 'boardScript':
      return 'Boards';
    case 'breaker':
      return names.board ?? 'Lineup';
  }
}

/** Whether this route needs the register loaded before it can render anything. */
export function needsRegister(route: Route): boolean {
  return route.screen !== 'projects' && route.screen !== 'target';
}
